"use client";

import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { CourseCard } from "@/components/courses/course-card";
import { getRelatedCoursesCopy } from "@/content/related-courses-copy";
import type { Locale } from "@/i18n-config";
import type { Course, RelatedCoursesSource } from "@/types";
import { cn } from "@/utils/cn";

type RelatedCoursesSectionProps = {
  title: string;
  courses: Course[];
  lang: Locale;
  source?: RelatedCoursesSource;
  placement?: "course_page" | "course_completion" | "progress_page";
  cardVariant?: "default" | "immersive";
  className?: string;
};

export function RelatedCoursesSection({
  title,
  courses,
  lang,
  source,
  placement,
  cardVariant = "default",
  className,
}: RelatedCoursesSectionProps) {
  const copy = getRelatedCoursesCopy(lang);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    loop: false,
    skipSnaps: false,
    dragFree: false,
    containScroll: "trimSnaps",
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(courses.length > 1);
  const isCourseCompletion = placement === "course_completion";

  useEffect(() => {
    if (!emblaApi) return;

    const syncScrollState = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };

    syncScrollState();
    emblaApi.on("select", syncScrollState);
    emblaApi.on("reInit", syncScrollState);

    return () => {
      emblaApi.off("select", syncScrollState);
      emblaApi.off("reInit", syncScrollState);
    };
  }, [emblaApi]);

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  if (courses.length === 0) {
    return null;
  }

  return (
    <section
      className={cn("space-y-4", className)}
      data-related-courses-placement={placement}
      data-related-courses-source={source}
    >
      <div className="flex items-center justify-between gap-3">
        <h2 className={cn("text-2xl font-semibold", isCourseCompletion ? "text-[#fffaf1]" : "text-[#1f2a27]")}>
          {title}
        </h2>

        {courses.length > 1 ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="related-courses-prev"
              onClick={scrollPrev}
              disabled={!canScrollPrev}
              aria-label={copy.previousCourses}
              className={cn(
                "inline-flex h-10 w-10 items-center justify-center rounded-full border transition-colors disabled:cursor-not-allowed disabled:opacity-45",
                isCourseCompletion
                  ? "border-[#d8cab2]/18 bg-white/[0.04] text-[#fffaf1] hover:border-[#0f766e]/30 hover:bg-white/[0.08]"
                  : "border-[#d1c4ad] bg-white text-[#29453f] hover:border-[#aa9471] hover:bg-[#fbf6eb]"
              )}
            >
              <ChevronLeft size={18} />
            </button>

            <button
              type="button"
              data-testid="related-courses-next"
              onClick={scrollNext}
              disabled={!canScrollNext}
              aria-label={copy.nextCourses}
              className={cn(
                "inline-flex h-10 w-10 items-center justify-center rounded-full border transition-colors disabled:cursor-not-allowed disabled:opacity-45",
                isCourseCompletion
                  ? "border-[#d8cab2]/18 bg-white/[0.04] text-[#fffaf1] hover:border-[#0f766e]/30 hover:bg-white/[0.08]"
                  : "border-[#d1c4ad] bg-white text-[#29453f] hover:border-[#aa9471] hover:bg-[#fbf6eb]"
              )}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        ) : null}
      </div>

      <div ref={emblaRef} className="overflow-hidden">
        <div className="flex touch-pan-y gap-4">
          {courses.map((course) => (
            <div key={course._id} className="min-w-0 flex-[0_0_100%] lg:flex-[0_0_calc((100%_-_2rem)/3)]">
              <CourseCard course={course} lang={lang} variant={cardVariant} className="h-full" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
